import React, { useState } from 'react';
import { RcFile } from 'antd/lib/upload';
import { useSetRecoilState } from 'recoil';

import DependenciesModal from '../DependenciesModal';
import ProgressPanel from '../ProgressPanel';
import { AgentModel, DataInterfaceType, ExchangeDependency, ExchangeObjectType } from '../../../gamma-api';
import { defaultApi } from '../../apis';
import IbIcon from '../common/IbIcon';
import { alertsSelectorAdd } from '../../recoil';
import { getTranslateFunc } from '../../utils/stringUtil';
import FileUpload from '../common/FileUpload';

import { buildDependenciesFile, hasActiveDependencies } from './utils';

interface IAgentImportProps {
  interfaceType: DataInterfaceType;
  folderId: string;
  agentId?: string;
  onFinish?: (agent: AgentModel) => Promise<void>;
}

const AgentImport: React.FC<IAgentImportProps> = ({ interfaceType, folderId, agentId, onFinish, children }) => {
  const translatePrefix = interfaceType === DataInterfaceType.Api ? 'API' : 'Chat';
  const translate = getTranslateFunc(`Agent: Import: ${translatePrefix}:`);

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [currentFiles, setCurrentFiles] = useState<RcFile[]>([]);
  const [currentDependencies, setCurrentDependencies] = useState<ExchangeDependency[]>([]);

  const [dependenciesModalVisible, setDependenciesModalVisible] = useState(false);
  const [progressPanelVisible, setProgressPanelVisible] = useState(false);

  const importAgent = async (file: RcFile, dependencies: ExchangeDependency[]) => {
    const response = agentId
      ? await defaultApi.importAgentUpdateForm(agentId, folderId, file, buildDependenciesFile(dependencies))
      : await defaultApi.importAgentCreateForm(folderId, file, buildDependenciesFile(dependencies));
    onFinish && (await onFinish(response.data.agent));
  };

  const onFileUpload = async (files: RcFile[]) => {
    setProgressPanelVisible(true);
    try {
      const file = files[0];
      const response = agentId
        ? await defaultApi.inspectAgentUpdateForm(agentId, folderId, file)
        : await defaultApi.inspectAgentCreateForm(folderId, file);
      const dependencies = response.data.dependencies;
      if (hasActiveDependencies(dependencies, 1)) {
        setCurrentFiles(files);
        setCurrentDependencies(dependencies);
        setDependenciesModalVisible(true);
      } else {
        await importAgent(file, dependencies);
      }
    } catch (e) {
      addAlert({
        content: translate('Error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      return;
    } finally {
      setProgressPanelVisible(false);
    }
    return false;
  };

  const onImportFinish = async () => {
    setDependenciesModalVisible(false);
    setProgressPanelVisible(true);
    try {
      await importAgent(currentFiles[0], currentDependencies);
    } catch (e) {
      addAlert({
        content: translate('Error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      return;
    } finally {
      setProgressPanelVisible(false);
      setCurrentDependencies([]);
      setCurrentFiles([]);
    }

    addAlert({
      content: translate('Success'),
      type: 'success',
    });
  };

  const onImportCancel = async () => {
    setDependenciesModalVisible(false);
    setCurrentDependencies([]);
    setCurrentFiles([]);
  };

  const onDependenciesChange = (dependencies: ExchangeDependency[]) => {
    setCurrentDependencies(dependencies);
  };

  return (
    <>
      <FileUpload noButton accept=".zip" disabled={false} multiple={false} onUpload={onFileUpload}>
        {children}
      </FileUpload>
      <DependenciesModal
        dependencies={currentDependencies}
        entityId={agentId}
        entityType={ExchangeObjectType.Agent}
        folderId={folderId}
        visible={dependenciesModalVisible}
        onCancel={onImportCancel}
        onChange={onDependenciesChange}
        onFinish={onImportFinish}
      />
      <ProgressPanel.AgentImport visible={progressPanelVisible} />
    </>
  );
};

export default AgentImport;
